/**
 * Simple API cache utility for client-side caching
 * Reduces API calls and improves loading performance
 */

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cache = new Map();

/**
 * Get cached data if available and not expired
 */
export const getCached = (key) => {
  const cached = cache.get(key);
  if (!cached) return null;
  
  const now = Date.now();
  if (now - cached.timestamp > CACHE_DURATION) {
    cache.delete(key);
    return null;
  }
  
  return cached.data;
};

/**
 * Set data in cache
 */
export const setCached = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

/**
 * Clear specific cache entry or all cache if no key provided
 */
export const clearCache = (key = null) => {
  if (key === null) {
    cache.clear();
  } else {
    cache.delete(key);
  }
};

/**
 * Clear all cache
 */
export const clearAllCache = () => {
  cache.clear();
};

/**
 * Create a cached API function
 */
export const createCachedApiCall = (apiFunction, cacheKey) => {
  return async (...args) => {
    const key = `${cacheKey}-${JSON.stringify(args)}`;
    
    // Check cache first
    const cached = getCached(key);
    if (cached !== null) {
      return cached;
    }
    
    // Make API call
    try {
      const data = await apiFunction(...args);
      setCached(key, data);
      return data;
    } catch (error) {
      // If error and we have cached data, return cached data as fallback
      const cachedFallback = getCached(key);
      if (cachedFallback !== null) {
        console.warn('API call failed, using cached data:', error);
        return cachedFallback;
      }
      throw error;
    }
  };
};

/**
 * Request deduplication - prevents multiple simultaneous requests for the same resource
 */
const pendingRequests = new Map();

export const deduplicateRequest = (key, requestFn) => {
  // If request is already pending, return the same promise
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }
  
  // Create new request
  const promise = requestFn()
    .finally(() => {
      // Remove from pending after completion
      pendingRequests.delete(key);
    });
  
  pendingRequests.set(key, promise);
  return promise;
};

